// components/agent-chat/types.ts
// Agent-specific types for the custom chat agent UI

import type { ScrapedProduct } from '@/lib/types';

export interface AgentChatWidgetProps {
  onClose: () => void;
  onSwitchToSearch: () => void;
  /** Shared with the search screens; owned by SearchWidget (lib/viewMode.ts). */
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
}

/** Simplified product shape returned by the searchProducts tool.
 *  The explicit fields below are always present; the index signature
 *  accommodates the dynamic facet array fields from `clientConfig.facetFields`
 *  (product_type, stock_status, screen_size, …) which the tool now spreads in
 *  when populated. */
export interface AgentProduct {
  id: string;
  title: string;
  price?: number;
  original_price?: number;
  brand: string | null;
  category: string | null;
  color: string[];
  material: string[];
  subcategory: string[];
  image_url: string;
  url: string;
  /** A collection set (kostaboda): no price; the card's button opens its page. */
  collection_set?: true;
  /** Dynamic facet array values, keyed by ClientConfig.facetFields[].field. */
  [key: string]: unknown;
}

/** Search params saved from the tool call, used for filter/pagination endpoint */
export interface AgentSearchParams {
  searchTerm: string;
  normalizedTerm: string;
  categories: string[];
  keywords: string[];
  filters: string[];
  excludeCategories: string[];
}

/** The result shape from the searchProducts tool */
export interface SearchToolResult {
  found: number;
  topCategories: string[];
  products: AgentProduct[];
  error?: string;
  searchParams?: AgentSearchParams;
  facetDistribution?: Record<string, Record<string, number>>;
}

/** Convert agent products to ScrapedProduct for reuse with existing components.
 *  Spreads dynamic facet array fields (anything beyond the explicit set below)
 *  onto the result so the agent filter sidebar can both display and filter
 *  by them. */
export function toScrapedProducts(products: AgentProduct[], clientId = 'parousiasi'): ScrapedProduct[] {
  return products.map(p => {
    const sp: ScrapedProduct & Record<string, unknown> = {
      id: p.id,
      client_id: clientId,
      title: p.title,
      description: null,
      url: p.url,
      image_url: p.image_url,
      price: p.price,
      original_price: p.original_price,
      ...(p.collection_set ? { collection_set: true as const } : {}),
      brand: p.brand,
      category: p.category,
      color: p.color || [],
      material: p.material || [],
      subcategory: p.subcategory || [],
      description_short: null,
      title_normalized: '',
      description_short_normalized: '',
      category_normalized: '',
      subcategory_normalized: '',
    };
    // Spread any dynamic string-array facet fields not already mapped above
    // (product_type, stock_status, screen_size, …).
    for (const [key, value] of Object.entries(p)) {
      if (key in sp) continue;
      if (Array.isArray(value) && value.every(v => typeof v === 'string')) {
        sp[key] = value;
      }
    }
    return sp;
  });
}
